import { useEffect, useRef } from "react";

type Props = {
    vidalyticsIds: string[],
    mobileVidalyticsIds: string[],
};

const TestimonialVideoCarousel = ({ vidalyticsIds, mobileVidalyticsIds }: Props) => {
    // Store player instances here
    const playersRef = useRef<Record<string, any>>({});
    // Keep track of injected scripts so we can clean up
    const scriptsRef = useRef<HTMLScriptElement[]>([]);

    useEffect(() => {
        /*** 1. Inject Vidalytics embeds ***/
        const embedScripts = vidalyticsIds.map((id) => {
            const script = document.createElement("script");
            script.type = "text/javascript";
            script.async = true;
            script.innerHTML = `
        (function(v,i,d,a,l,y,t,c,s){
          y='_'+d.toLowerCase(); c=d+'L';
          if(!v[d]){v[d]={};} if(!v[c]){v[c]={};} if(!v[y]){v[y]={};}
          var vl='Loader', vli=v[y][vl], vsl=v[c][vl+'Script'], vlf=v[c][vl+'Loaded'], ve='Embed';
          if(!vsl){
            vsl=function(u,cb){
              if(t){cb();return;}
              s=i.createElement("script"); s.type="text/javascript"; s.async=1; s.src=u;
              if(s.readyState){
                s.onreadystatechange=function(){
                  if(s.readyState==='loaded'||s.readyState==='complete'){
                    s.onreadystatechange=null; vlf=1; cb();
                  }
                };
              } else {
                s.onload=function(){ vlf=1; cb(); };
              }
              i.getElementsByTagName("head")[0].appendChild(s);
            };
          }
          vsl(l+'loader.min.js',function(){
            if(!vli){ var vlc=v[c][vl]; vli=new vlc(); }
            vli.loadScript(l+'player.min.js',function(){
              var vec=v[d][ve]; t=new vec(); t.run(a);
            });
          });
        })(window, document, 'Vidalytics', 'vidalytics_embed_${id}',
           'https://fast.vidalytics.com/embeds/g6Z8uQvx/${id}/');
      `;
            document.body.appendChild(script);
            return script;
        });
        scriptsRef.current.push(...embedScripts);

        /*** 2. Define the Player API wrapper ***/
        (function (v, a, p, i) {
            v.getVidalyticsPlayer = (embedId: string) => {
                v[a] = v[a] || {};
                v[a][p] = v[a][p] || {};
                let entry = (v[a][p][embedId] = v[a][p][embedId] || {});
                return new Promise<any>((resolve) => {
                    if (entry[i]) return void resolve(entry[i]);
                    let playerInstance: any;
                    Object.defineProperty(entry, i, {
                        get: () => playerInstance,
                        set: (inst) => {
                            playerInstance = inst;
                            resolve(inst);
                        }
                    });
                });
            };
        })(window as any, "_vidalytics", "embeds", "player");

        /*** 3. Collect player instances ***/
        [...vidalyticsIds, ...mobileVidalyticsIds].forEach((id) => {
            const embedId = `vidalytics_embed_${id}`;
            (window as any)
                .getVidalyticsPlayer(embedId)
                .then((player: any) => {
                    playersRef.current[id] = player;
                })
                .catch(() => {
                    // if this embed never exposes the API, we'll fallback to <video> later
                });
        });

        /*** 4. Pause *all* on carousel slide ***/
        const carouselEl = document.getElementById("testimonialCarousel");
        const handleSlide = () => {
            [...vidalyticsIds, ...mobileVidalyticsIds].forEach((id) => {
                // Try the Vidalytics API pause()
                const player = playersRef.current[id];
                if (player && typeof player.pause === "function") {
                    player.pause();
                    return;
                }
                // Fallback: find a <video> in the embed container
                const container = document.getElementById(`vidalytics_embed_${id}`);
                const html5vid = container?.querySelector("video") as HTMLVideoElement | null;
                if (html5vid) {
                    html5vid.pause();
                }
            });
        };

        carouselEl?.addEventListener("slide.bs.carousel", handleSlide);

        /*** 5. Cleanup ***/
        return () => {
            scriptsRef.current.forEach((s) => document.body.removeChild(s));
            scriptsRef.current = [];
            carouselEl?.removeEventListener("slide.bs.carousel", handleSlide);
        };
    }, [vidalyticsIds]);

    return (
        <section id="testimonial-video" className="gg-bg-solid-green-light">
            <div className="container-fluid pt-4 pb-4 text-center">
                <div className="row align-items-center justify-content-center">
                    <div className="col-md-9 col-sm-10 col-11">
                        <div
                            id="testimonialCarousel"
                            className="carousel slide"
                            data-bs-ride="false"
                            data-bs-interval="0"
                        >
                            <div className="carousel-inner">
                                {vidalyticsIds.map((videoId, idx) => (
                                    <div
                                        className={`carousel-item ${idx === 0 ? "active" : ""}`}
                                        key={videoId}
                                    >
                                        <div id={`vidalytics_embed_${videoId}`}></div>
                                    </div>
                                ))}
                            </div>
                            <button
                                className="carousel-control-prev"
                                type="button"
                                data-bs-target="#testimonialCarousel"
                                data-bs-slide="prev"
                            >
                                <span className="carousel-control-prev-icon"></span>
                            </button>
                            <button
                                className="carousel-control-next"
                                type="button"
                                data-bs-target="#testimonialCarousel"
                                data-bs-slide="next"
                            >
                                <span className="carousel-control-next-icon"></span>
                            </button>
                            <div className="carousel-indicators">
                                {vidalyticsIds.map((videoId, idx) => (
                                    <button
                                        key={videoId}
                                        type="button"
                                        data-bs-target="#testimonialCarousel"
                                        data-bs-slide-to={idx}
                                        className={idx === 0 ? "active" : ""}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TestimonialVideoCarousel;
